import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import Autocomplete from "@material-ui/lab/Autocomplete"
import { Add, Delete, Map, Save } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import useAppStore from "../../../stores/AppStore"
import { AtribuirRegiaoActionsTypes } from "../context/atribuirRegiaoActions"
import { useAtribuirRegiaoContext } from "../context/useAtribuirRegiaoContext"
import type { BairroRegiao, CidadeView } from "../models/atribuirRegiaoModel"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "1600px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    header: {
        marginBottom: theme.spacing(4),
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "flex-start",
            marginBottom: theme.spacing(3),
        },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    tableHead: {
        backgroundColor: "#f8f9fa",
    },
    tableHeadCell: {
        fontWeight: 600,
        color: "#2c3e50",
        whiteSpace: "nowrap",
    },
    tableInput: {
        "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            "& fieldset": { borderColor: "#e0e0e0" },
            "&:hover fieldset": { borderColor: "#4caf50" },
            "&.Mui-focused fieldset": { borderColor: "#4caf50" },
        },
    },
    deleteButton: {
        color: "var(--cor-erro)",
        "&:hover": { backgroundColor: "rgba(var(--cor-erro-rgb),0.08)" },
    },
    addButton: {
        borderRadius: 20,
        borderColor: "#4caf50",
        color: "#4caf50",
        fontWeight: 600,
        "&:hover": {
            borderColor: "#388e3c",
            backgroundColor: "rgba(var(--cor-botao-salvar-rgb),0.08)",
        },
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        padding: theme.spacing(1.5, 4),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(var(--cor-botao-salvar-rgb), 0.3)",
        minWidth: "180px",
        "&:hover": {
            backgroundColor: "var(--cor-botao-salvar-hover)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(var(--cor-botao-salvar-rgb), 0.4)",
        },
        [theme.breakpoints.down("sm")]: { minWidth: "100%", padding: theme.spacing(2, 3) },
    },
    cidadeButton: {
        borderRadius: 20,
        fontWeight: 600,
        "&:hover": { backgroundColor: "rgba(var(--cor-botao-salvar-rgb),0.08)" },
    },
    buttonRow: {
        display: "flex",
        gap: theme.spacing(2),
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        marginTop: theme.spacing(3),
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            gap: theme.spacing(1.5),
        },
    },
    mobileCard: {
        borderRadius: 15,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        border: "1px solid #e9ecef",
    },
    mobileCardRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },
    mobileField: { flex: 1 },
    cidadeChip: {
        display: "inline-block",
        backgroundColor: "#e8f5e9",
        color: "#2e7d32",
        padding: "4px 12px",
        borderRadius: 20,
        fontWeight: 600,
        fontSize: "0.85rem",
        margin: theme.spacing(0.5),
    },
}))

export function BairrosForm() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, dispatch, salvar, removerBairro, adicionarCidade, removerCidade, erroCarregamento } =
        useAtribuirRegiaoContext()
    const { enqueueSnackbar } = useSnackbar()
    const configuracoes = useAppStore(s => s.configuracoes)
    const mostrarNoturna = configuracoes?.user?.parametros?.permiteTaxaEntregaNoturna ?? false

    const { bairros, cidadesEstabelecimento, todasCidades } = dados

    useEffect(() => {
        if (erroCarregamento) {
            enqueueSnackbar("Erro ao carregar bairros", { variant: "error" })
        }
    }, [erroCarregamento])

    const handleCampo = (
        index: number,
        campo: keyof BairroRegiao,
        valor: string | number | null
    ) => {
        dispatch({ type: AtribuirRegiaoActionsTypes.ATUALIZAR_BAIRRO, payload: { index, campo, valor } })
    }

    const handleCidade = (index: number, idCidade: number | null) => {
        const cidade = todasCidades.find(c => c.id === idCidade) ?? null
        dispatch({
            type: AtribuirRegiaoActionsTypes.ATUALIZAR_BAIRRO,
            payload: { index, campo: "idCidade", valor: idCidade },
        })
        dispatch({
            type: AtribuirRegiaoActionsTypes.ATUALIZAR_BAIRRO,
            payload: { index, campo: "nomeCidade", valor: cidade?.nome ?? null },
        })
    }

    const [confirmacaoAberta, setConfirmacaoAberta] = useState(false)
    const [indexParaRemover, setIndexParaRemover] = useState<number | null>(null)

    const handleRemover = (index: number) => {
        setIndexParaRemover(index)
        setConfirmacaoAberta(true)
    }

    const handleConfirmarRemocao = async () => {
        if (indexParaRemover === null) return
        const bairro = bairros[indexParaRemover]
        setConfirmacaoAberta(false)
        setIndexParaRemover(null)
        try {
            await removerBairro(indexParaRemover, bairro)
            enqueueSnackbar("Bairro removido com sucesso!", { variant: "success" })
        } catch (error) {
            enqueueSnackbar((error as Error).message || "Erro ao remover bairro", { variant: "error" })
        }
    }

    const handleCancelarRemocao = () => {
        setConfirmacaoAberta(false)
        setIndexParaRemover(null)
    }

    const handleSalvar = async () => {
        const algumSemBairro = bairros.some(b => !b.nomeBairro.trim())
        if (algumSemBairro) {
            enqueueSnackbar("Preencha o nome do bairro em todas as linhas", { variant: "error" })
            return
        }
        const algumSemTaxa = bairros.some(b => !b.taxaEntrega.trim())
        if (algumSemTaxa) {
            enqueueSnackbar("Preencha a taxa de entrega em todas as linhas", { variant: "error" })
            return
        }
        const algumSemCidade = bairros.some(b => !b.idCidade)
        if (algumSemCidade) {
            enqueueSnackbar("Selecione a cidade em todas as linhas", { variant: "error" })
            return
        }
        try {
            await salvar()
            enqueueSnackbar("Bairros salvos com sucesso!", { variant: "success" })
        } catch (error) {
            enqueueSnackbar((error as Error).message || "Erro ao salvar bairros", { variant: "error" })
        }
    }

    // Modal de gerenciamento de cidades
    const [modalCidadeAberto, setModalCidadeAberto] = useState(false)
    const [modalRemoverCidadeAberto, setModalRemoverCidadeAberto] = useState(false)
    const [cidadeSelecionadaAdicionar, setCidadeSelecionadaAdicionar] = useState<CidadeView | null>(null)
    const [cidadeSelecionadaRemover, setCidadeSelecionadaRemover] = useState<number | "">("")

    const handleAdicionarCidade = async () => {
        if (!cidadeSelecionadaAdicionar) return
        try {
            await adicionarCidade(cidadeSelecionadaAdicionar.id)
            enqueueSnackbar("Cidade adicionada com sucesso!", { variant: "success" })
            setModalCidadeAberto(false)
            setCidadeSelecionadaAdicionar(null)
        } catch (error) {
            enqueueSnackbar((error as Error).message || "Erro ao adicionar cidade", { variant: "error" })
        }
    }

    const handleRemoverCidade = async () => {
        if (!cidadeSelecionadaRemover) return
        try {
            await removerCidade(cidadeSelecionadaRemover as number)
            enqueueSnackbar("Cidade removida com sucesso!", { variant: "success" })
            setModalRemoverCidadeAberto(false)
            setCidadeSelecionadaRemover("")
        } catch (error) {
            enqueueSnackbar((error as Error).message || "Erro ao remover cidade", { variant: "error" })
        }
    }

    const textField = (
        index: number,
        campo: keyof BairroRegiao,
        placeholder: string,
        required?: boolean
    ) => (
        <TextField
            className={classes.tableInput}
            variant="outlined"
            size="small"
            fullWidth
            placeholder={placeholder}
            value={(bairros[index][campo] as string) ?? ""}
            onChange={e => handleCampo(index, campo, e.target.value || null)}
            disabled={!!erroCarregamento}
            error={required && !(bairros[index][campo] as string)?.trim()}
        />
    )

    const moneyField = (
        index: number,
        campo: keyof BairroRegiao,
        placeholder: string,
        required?: boolean
    ) => {
        const rawValue = (bairros[index][campo] as string) ?? ""
        return (
            <TextField
                className={classes.tableInput}
                variant="outlined"
                size="small"
                fullWidth
                placeholder={placeholder}
                value={rawValue.replace(".", ",")}
                onChange={e => {
                    const valor = e.target.value.replace(".", ",")
                    handleCampo(index, campo, valor || null)
                }}
                disabled={!!erroCarregamento}
                error={required && !rawValue.trim()}
            />
        )
    }

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <Box className={classes.header}>
                    <Box>
                        <Typography variant="h4" className={classes.titleText}>
                            Bairros Atendidos
                        </Typography>
                        <Typography variant="subtitle1" style={{ color: "#7f8c8d", marginTop: 4 }}>
                            Cadastre aqui os bairros que seu delivery atende
                        </Typography>
                    </Box>
                    <Box display="flex" gridGap={8} flexWrap="wrap">
                        <Button
                            variant="outlined"
                            className={classes.cidadeButton}
                            style={{ borderColor: "#4caf50", color: "#4caf50" }}
                            onClick={() => setModalCidadeAberto(true)}
                            disabled={!!erroCarregamento}
                        >
                            Adicionar Cidade
                        </Button>
                        <Button
                            variant="outlined"
                            className={classes.cidadeButton}
                            style={{ borderColor: "var(--cor-erro)", color: "var(--cor-erro)" }}
                            onClick={() => setModalRemoverCidadeAberto(true)}
                            disabled={!!erroCarregamento || cidadesEstabelecimento.length === 0}
                        >
                            Remover Cidade
                        </Button>
                    </Box>
                </Box>

                {cidadesEstabelecimento.length > 0 && (
                    <Box mb={2} display="flex" flexWrap="wrap" alignItems="center">
                        <Typography
                            variant="body2"
                            style={{ color: "#7f8c8d", marginRight: 8, marginBottom: 4 }}
                        >
                            Cidades atendidas:
                        </Typography>
                        {cidadesEstabelecimento.map(c => (
                            <span key={c.id} className={classes.cidadeChip}>
                                {c.nome}
                            </span>
                        ))}
                    </Box>
                )}

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Map className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Bairros cadastrados
                        </Typography>
                    </Box>

                    {!isMobile ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead className={classes.tableHead}>
                                    <TableRow>
                                        <TableCell className={classes.tableHeadCell}>Cidade</TableCell>
                                        <TableCell className={classes.tableHeadCell}>Bairro</TableCell>
                                        <TableCell className={classes.tableHeadCell}>
                                            Taxa de Entrega (R$)
                                        </TableCell>
                                        {mostrarNoturna && (
                                            <TableCell className={classes.tableHeadCell}>
                                                Taxa Noturna (R$)
                                            </TableCell>
                                        )}
                                        <TableCell className={classes.tableHeadCell}>
                                            Mín. p/ Grátis (R$)
                                        </TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {bairros.length === 0 && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={mostrarNoturna ? 6 : 5}
                                                align="center"
                                                style={{ color: "#7f8c8d", padding: 24 }}
                                            >
                                                Nenhum bairro cadastrado. Clique em "Novo Bairro"
                                                para adicionar.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {bairros.map((bairro, index) => (
                                        <TableRow key={bairro.id ?? `novo-${index}`}>
                                            <TableCell style={{ minWidth: 160 }}>
                                                <FormControl
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    error={!bairro.idCidade}
                                                    disabled={!!erroCarregamento}
                                                >
                                                    <Select
                                                        value={bairro.idCidade ?? ""}
                                                        onChange={e =>
                                                            handleCidade(
                                                                index,
                                                                (e.target.value as number) || null
                                                            )
                                                        }
                                                        displayEmpty
                                                    >
                                                        <MenuItem value="">
                                                            <em>Selecione...</em>
                                                        </MenuItem>
                                                        {cidadesEstabelecimento.map(c => (
                                                            <MenuItem key={c.id} value={c.id}>
                                                                {c.nome}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell style={{ minWidth: 160 }}>
                                                {textField(index, "nomeBairro", "Ex: Centro", true)}
                                            </TableCell>
                                            <TableCell style={{ minWidth: 130 }}>
                                                {moneyField(index, "taxaEntrega", "Ex: 5,00", true)}
                                            </TableCell>
                                            {mostrarNoturna && (
                                                <TableCell style={{ minWidth: 130 }}>
                                                    {moneyField(
                                                        index,
                                                        "taxaEntregaNoturna",
                                                        "Ex: 7,00"
                                                    )}
                                                </TableCell>
                                            )}
                                            <TableCell style={{ minWidth: 130 }}>
                                                {moneyField(
                                                    index,
                                                    "minimoEntregaGratis",
                                                    "Ex: 50,00"
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Remover bairro">
                                                    <IconButton
                                                        size="small"
                                                        aria-label="Remover bairro"
                                                        className={classes.deleteButton}
                                                        onClick={() => handleRemover(index)}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box style={{ padding: 16 }}>
                            {bairros.length === 0 && (
                                <Typography
                                    align="center"
                                    style={{ color: "#7f8c8d", padding: "16px 0" }}
                                >
                                    Nenhum bairro cadastrado. Clique em "Novo Bairro" para
                                    adicionar.
                                </Typography>
                            )}
                            {bairros.map((bairro, index) => (
                                <Box key={bairro.id ?? `novo-${index}`} className={classes.mobileCard}>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            style={{ fontWeight: 600, color: "#2c3e50" }}
                                        >
                                            Bairro {index + 1}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            aria-label="Remover bairro"
                                            className={classes.deleteButton}
                                            onClick={() => handleRemover(index)}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <Box className={classes.mobileCardRow}>
                                        <Box flex={1}>
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    color: "#7f8c8d",
                                                    display: "block",
                                                    marginBottom: 4,
                                                }}
                                            >
                                                Cidade
                                            </Typography>
                                            <FormControl
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                error={!bairro.idCidade}
                                                disabled={!!erroCarregamento}
                                            >
                                                <Select
                                                    value={bairro.idCidade ?? ""}
                                                    onChange={e =>
                                                        handleCidade(
                                                            index,
                                                            (e.target.value as number) || null
                                                        )
                                                    }
                                                    displayEmpty
                                                >
                                                    <MenuItem value="">
                                                        <em>Selecione...</em>
                                                    </MenuItem>
                                                    {cidadesEstabelecimento.map(c => (
                                                        <MenuItem key={c.id} value={c.id}>
                                                            {c.nome}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    </Box>
                                    <Box className={classes.mobileCardRow}>
                                        <Box className={classes.mobileField}>
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    color: "#7f8c8d",
                                                    display: "block",
                                                    marginBottom: 4,
                                                }}
                                            >
                                                Bairro
                                            </Typography>
                                            {textField(index, "nomeBairro", "Ex: Centro", true)}
                                        </Box>
                                        <Box className={classes.mobileField}>
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    color: "#7f8c8d",
                                                    display: "block",
                                                    marginBottom: 4,
                                                }}
                                            >
                                                Taxa de Entrega (R$)
                                            </Typography>
                                            {moneyField(index, "taxaEntrega", "Ex: 5,00", true)}
                                        </Box>
                                    </Box>
                                    <Box className={classes.mobileCardRow}>
                                        {mostrarNoturna && (
                                            <Box className={classes.mobileField}>
                                                <Typography
                                                    variant="caption"
                                                    style={{
                                                        color: "#7f8c8d",
                                                        display: "block",
                                                        marginBottom: 4,
                                                    }}
                                                >
                                                    Taxa Noturna (R$)
                                                </Typography>
                                                {moneyField(
                                                    index,
                                                    "taxaEntregaNoturna",
                                                    "Ex: 7,00"
                                                )}
                                            </Box>
                                        )}
                                        <Box className={classes.mobileField}>
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    color: "#7f8c8d",
                                                    display: "block",
                                                    marginBottom: 4,
                                                }}
                                            >
                                                Mín. p/ Grátis (R$)
                                            </Typography>
                                            {moneyField(index, "minimoEntregaGratis", "Ex: 50,00")}
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Paper>

                <Box className={classes.buttonRow}>
                    <Button
                        variant="outlined"
                        className={classes.addButton}
                        startIcon={<Add />}
                        onClick={() => dispatch({ type: AtribuirRegiaoActionsTypes.ADICIONAR_BAIRRO })}
                        disabled={!!erroCarregamento}
                    >
                        Novo Bairro
                    </Button>
                    <Button
                        className={classes.saveButton}
                        startIcon={!isMobile && <Save />}
                        onClick={handleSalvar}
                        disabled={!!erroCarregamento}
                    >
                        {isMobile ? "💾 Salvar Bairros" : "Salvar Bairros"}
                    </Button>
                </Box>
            </div>

            {/* Confirmação de remoção de bairro */}
            <Dialog open={confirmacaoAberta} onClose={handleCancelarRemocao}>
                <DialogTitle>Remover bairro</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Tem certeza que deseja remover este bairro? Esta ação não pode ser desfeita.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelarRemocao} color="default">
                        Cancelar
                    </Button>
                    <Button onClick={handleConfirmarRemocao} style={{ color: "var(--cor-erro)" }}>
                        Remover
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal adicionar cidade */}
            <Dialog
                open={modalCidadeAberto}
                onClose={() => { setModalCidadeAberto(false); setCidadeSelecionadaAdicionar(null) }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Adicionar Cidade</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        options={todasCidades}
                        getOptionLabel={c => c.nomeCompleto ?? c.nome}
                        value={cidadeSelecionadaAdicionar}
                        onChange={(_, value) => setCidadeSelecionadaAdicionar(value)}
                        noOptionsText="Nenhuma cidade encontrada"
                        filterOptions={(options, { inputValue }) => {
                            const termo = inputValue.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
                            if (!termo) return []
                            return options.filter(c => {
                                const nome = c.nome.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
                                return nome.startsWith(termo)
                            })
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Digite o nome da cidade"
                                variant="outlined"
                                size="small"
                                style={{ marginTop: 8 }}
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => { setModalCidadeAberto(false); setCidadeSelecionadaAdicionar(null) }}
                        color="default"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleAdicionarCidade}
                        style={{ color: "#4caf50" }}
                        disabled={!cidadeSelecionadaAdicionar}
                    >
                        Adicionar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal remover cidade */}
            <Dialog
                open={modalRemoverCidadeAberto}
                onClose={() => setModalRemoverCidadeAberto(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Remover Cidade</DialogTitle>
                <DialogContent>
                    <FormControl variant="outlined" size="small" fullWidth style={{ marginTop: 8 }}>
                        <InputLabel>Selecione uma cidade</InputLabel>
                        <Select
                            value={cidadeSelecionadaRemover}
                            onChange={e => setCidadeSelecionadaRemover(e.target.value as number)}
                            label="Selecione uma cidade"
                        >
                            {cidadesEstabelecimento.map(c => (
                                <MenuItem key={c.id} value={c.id}>
                                    {c.nome}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalRemoverCidadeAberto(false)} color="default">
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleRemoverCidade}
                        style={{ color: "var(--cor-erro)" }}
                        disabled={!cidadeSelecionadaRemover}
                    >
                        Remover
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
